import apiService from './apiService';

const ORDERS_STORAGE_KEY = 'rahber_showroom_orders';

export function getLocalOrders() {
    try {
        const raw = localStorage.getItem(ORDERS_STORAGE_KEY);
        if (!raw) return [];
        return JSON.parse(raw);
    } catch (e) {
        console.error('Error reading local orders:', e);
        return [];
    }
}

export function saveLocalOrder(order) {
    try {
        const existing = getLocalOrders();
        const updated = [order, ...existing.filter((o) => o.id !== order.id)];
        localStorage.setItem(ORDERS_STORAGE_KEY, JSON.stringify(updated));
        return updated;
    } catch (e) {
        console.error('Error saving local order:', e);
        return [];
    }
}

export function updateOrderStatus(id, newStatus) {
    try {
        const existing = getLocalOrders();
        const updated = existing.map((o) =>
            o.id === id ? { ...o, status: newStatus, updatedAt: new Date().toISOString() } : o
        );
        localStorage.setItem(ORDERS_STORAGE_KEY, JSON.stringify(updated));
        return updated;
    } catch (e) {
        console.error('Error updating order status:', e);
        return [];
    }
}

export function deleteLocalOrder(id) {
    try {
        const existing = getLocalOrders();
        const updated = existing.filter((o) => o.id !== id);
        localStorage.setItem(ORDERS_STORAGE_KEY, JSON.stringify(updated));
        return updated;
    } catch (e) {
        console.error('Error deleting local order:', e);
        return [];
    }
}

class StoreOrderService {
    /**
     * Submit order to the backend API and persist order in database.
     * @param {object} payload - Complete customer and order information
     * @returns {Promise<{ success: boolean, order: object }>} Created order confirmation
     */
    async placeCashOrder(payload) {
        // Send order directly to backend API
        const response = await apiService.post('/ecommerce/orders', payload);

        // Check if backend returned explicit failure status
        if (response && response.success === false) {
            throw new Error(response.message || response.error || 'The server could not process this order.');
        }

        // Extract order data across standard response envelopes
        const orderData =
            response?.data?.order ||
            response?.order ||
            response?.data ||
            response;

        if (!orderData || typeof orderData !== 'object') {
            throw new Error('Invalid response received from order server.');
        }

        const orderId =
            orderData.order_number ||
            orderData.id ||
            orderData._id ||
            orderData.orderId ||
            payload.order_number ||
            'ORD-' + Math.floor(100000 + Math.random() * 900000).toString();

        const confirmedOrder = {
            ...payload,
            ...orderData,
            id: orderId,
            order_number: orderId,
            _id: orderData._id || orderId,
            status: orderData.status || payload.status || 'Pending',
            createdAt: orderData.createdAt || orderData.created_at || new Date().toISOString(),
        };

        // Cache confirmed order locally for showroom portal
        saveLocalOrder(confirmedOrder);

        return {
            success: true,
            order: confirmedOrder,
        };
    }

    /**
     * Fetch orders from the backend API, falling back to locally saved orders if offline.
     * @param {object} params
     * @returns {Promise<Array>} List of orders
     */
    async getAllOrders(params = {}) {
        try {
            const response = await apiService.get('/ecommerce/orders', params);
            if (response && response.success === false) {
                return getLocalOrders();
            }
            const list =
                response?.data?.orders ||
                response?.orders ||
                response?.data ||
                [];
            if (Array.isArray(list) && list.length > 0) {
                // Merge with local orders
                list.forEach((ord) => saveLocalOrder(ord));
                return list;
            }
            return getLocalOrders();
        } catch (e) {
            console.warn('Could not fetch remote orders, using local storage:', e);
            return getLocalOrders();
        }
    }
}

const storeOrderService = new StoreOrderService();
export default storeOrderService;
